/**
 * 팩 오프닝 SFX — WebAudio 신스(에셋 0개). 찢기/플립/등급별 리빌/레어 스팅/
 * 요약 사운드를 오실레이터+노이즈로 합성한다. AudioContext는 유저 제스처
 * (팩 찢기)에서 unlock, on/off는 localStorage 영속(기본 ON).
 * 실패는 전부 무음 no-op — 사운드가 오프닝을 깨는 일은 없다.
 */

const STORAGE_KEY = "ty.reveal.sfx.v1";

let ctx: AudioContext | null = null;
let enabled: boolean | null = null;

function loadEnabled(): boolean {
  if (enabled !== null) return enabled;
  try {
    enabled = localStorage.getItem(STORAGE_KEY) !== "0";
  } catch {
    enabled = true;
  }
  return enabled;
}

export function sfxEnabled(): boolean {
  return loadEnabled();
}

export function setSfxEnabled(next: boolean): void {
  enabled = next;
  try {
    localStorage.setItem(STORAGE_KEY, next ? "1" : "0");
  } catch {
    // 프라이빗 모드 — 세션 내 값만 유지
  }
}

function context(): AudioContext | null {
  if (typeof window === "undefined") return null;
  try {
    if (!ctx) ctx = new AudioContext();
    return ctx;
  } catch {
    return null;
  }
}

/** 유저 제스처 안에서 호출 — suspended 컨텍스트 재개(자동재생 정책) */
export function unlockSfx(): void {
  const c = context();
  if (c && c.state === "suspended") void c.resume().catch(() => {});
}

type ToneOpts = {
  type?: OscillatorType;
  from: number;
  to?: number;
  at?: number;
  dur: number;
  gain?: number;
};

function tone(c: AudioContext, { type = "sine", from, to, at = 0, dur, gain = 0.1 }: ToneOpts) {
  const t0 = c.currentTime + at;
  const osc = c.createOscillator();
  const g = c.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(from, t0);
  if (to && to !== from) osc.frequency.exponentialRampToValueAtTime(to, t0 + dur);
  g.gain.setValueAtTime(0.0001, t0);
  g.gain.linearRampToValueAtTime(gain, t0 + 0.014);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
  osc.connect(g).connect(c.destination);
  osc.start(t0);
  osc.stop(t0 + dur + 0.06);
}

type NoiseOpts = { at?: number; dur: number; gain?: number; from?: number; to?: number; q?: number };

function noiseSweep(c: AudioContext, { at = 0, dur, gain = 0.1, from = 1400, to = 320, q = 0.9 }: NoiseOpts) {
  const t0 = c.currentTime + at;
  const length = Math.max(1, Math.floor(c.sampleRate * dur));
  const buffer = c.createBuffer(1, length, c.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < length; i += 1) data[i] = Math.random() * 2 - 1;
  const src = c.createBufferSource();
  src.buffer = buffer;
  const filter = c.createBiquadFilter();
  filter.type = "bandpass";
  filter.Q.value = q;
  filter.frequency.setValueAtTime(from, t0);
  filter.frequency.exponentialRampToValueAtTime(Math.max(40, to), t0 + dur);
  const g = c.createGain();
  g.gain.setValueAtTime(gain, t0);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
  src.connect(filter).connect(g).connect(c.destination);
  src.start(t0);
  src.stop(t0 + dur + 0.05);
}

function play(fn: (c: AudioContext) => void): void {
  if (!loadEnabled()) return;
  const c = context();
  if (!c) return;
  if (c.state === "suspended") void c.resume().catch(() => {});
  try {
    fn(c);
  } catch {
    // 무음 실패
  }
}

export const revealSfx = {
  /** 팩 찢기 — 노이즈 립 + 저음 썸프 */
  tear(): void {
    play((c) => {
      noiseSweep(c, { dur: 0.3, gain: 0.18, from: 1800, to: 240, q: 0.7 });
      tone(c, { type: "triangle", from: 130, to: 55, dur: 0.26, gain: 0.12 });
    });
  },

  /** 카드 플립 시작 — 짧은 휙 */
  flip(): void {
    play((c) => {
      noiseSweep(c, { dur: 0.09, gain: 0.05, from: 900, to: 2400, q: 1.4 });
      tone(c, { type: "sine", from: 460, to: 760, dur: 0.1, gain: 0.045 });
    });
  },

  /** 리빌 착지 — 등급이 높을수록 노트가 쌓인다 (tier 0~3) */
  reveal(tier: number): void {
    play((c) => {
      const notes =
        tier <= 0 ? [659] : tier === 1 ? [523, 784] : [523, 659, 988];
      notes.forEach((freq, i) => {
        tone(c, { type: "sine", from: freq, at: i * 0.07, dur: 0.34, gain: 0.075 });
      });
      if (tier >= 2) noiseSweep(c, { at: 0.05, dur: 0.4, gain: 0.03, from: 5200, to: 8200, q: 2.2 });
    });
  },

  /** 하이퍼레어 스팅 — 스웰 + 코드 + 쉬머 */
  rareSting(): void {
    play((c) => {
      tone(c, { type: "sawtooth", from: 90, to: 220, dur: 0.7, gain: 0.05 });
      [440, 554, 659, 880].forEach((freq, i) => {
        tone(c, { type: "triangle", from: freq, at: 0.12 + i * 0.09, dur: 0.85, gain: 0.06 });
      });
      noiseSweep(c, { at: 0.15, dur: 0.9, gain: 0.035, from: 6000, to: 9500, q: 2.6 });
    });
  },

  /** 요약 그리드 진입 — 부드러운 종결 2노트 */
  summary(): void {
    play((c) => {
      tone(c, { type: "sine", from: 392, dur: 0.3, gain: 0.05 });
      tone(c, { type: "sine", from: 523, at: 0.09, dur: 0.4, gain: 0.055 });
    });
  },
};
